import { z } from 'zod';

/**
 * The upper bound on any one weight.
 *
 * Weights are **relative**, not absolute: only the ratios between them change
 * the order a member is shown. A ceiling exists so a mistyped `300000` cannot
 * silently reduce every other term to noise while still looking like a valid
 * configuration — the operator gets a 422 naming the field instead of a
 * nomination list that quietly ranks on one signal.
 */
const MAX_WEIGHT = 1_000;

const WeightSchema = z.number().int().min(0).max(MAX_WEIGHT);

/**
 * The **structural** terms of the nomination score — the ones that are facts
 * about a member rather than answers to a staff-authored question (P16b).
 *
 * These four can be weighted from the day the platform boots because the
 * platform itself produces their inputs: `member_profiles.completionPct`,
 * `users.lastActiveAt`, an age preference, and the overlap between two
 * multi-select answers. Everything else a market matches on is a **question**,
 * and its weight lives in {@link NominationQuestionWeightsSchema} — see there
 * for why the two cannot be one map.
 *
 * The defaults in the registry are `02-build.md`'s own numbers (completion +10,
 * active-7d +15, age-pref fit +20, Jaccard up to +10), expressed as what they
 * always were: **defaults, not law** (U9). An operator who wants recency to
 * outrank everything sets it here, in one place, without a deployment.
 *
 * `sharedOptionsMax` is a **maximum** rather than a flat award: the term is
 * `jaccard × sharedOptionsMax`, so two members with identical multi-select
 * answers score the full weight and two with half the overlap score half of it.
 * Naming it `…Max` is what stops a future reader reading it as "+10 if they
 * share anything".
 *
 * Gender-scopable in the registry (U15): what men and women are matched on may
 * legitimately differ, and that must never be an `if (gender === …)` in the
 * scorer.
 */
export const NominationWeightsSchema = z
  .object({
    /** Scaled by `completionPct / 100`, so a half-finished profile earns half. */
    completion: WeightSchema,
    /** Awarded in full to a member active within `nominations.recentActivityDays`. */
    recentActivity: WeightSchema,
    /** Awarded in full when the two members' stated age ranges fit each other **both** ways. */
    agePreference: WeightSchema,
    /** The ceiling of the Jaccard term, not a flat award — see the class comment. */
    sharedOptionsMax: WeightSchema,
  })
  .strict();

export type NominationWeights = z.infer<typeof NominationWeightsSchema>;

/**
 * What one **question** is worth when two members gave the same answer, keyed by
 * the question's stable `key`.
 *
 * ## Why this is an open map and not a fixed object
 *
 * The profile is a staff-authored question tree (A4). Only `country` is seeded;
 * there is no `city` question and no age-preference question in the tree, so
 * `02-build.md`'s "same city +15" **cannot** be a named property — a market that
 * does not ask for a city would carry a dead scoring term, and a market that
 * asks for tribe, sect or profession could not weight it at all without a code
 * change (U9, U16).
 *
 * So the keys are **data**: whatever the operator's questions are called. That
 * is precisely the shape the admin's `key-value` control exists for, which is
 * why this needs no hand-off to another module's editor.
 *
 * ## Two things it deliberately does NOT do
 *
 * - **It does not decide which questions may be weighted.** That is
 *   `profile_questions.isMatchable`, the column and index P6 added *for this
 *   phase*. A weight naming a question that is absent, inactive or not matchable
 *   contributes **nothing** — silently, because a market retiring a question
 *   must not start returning 422s to members.
 * - **It does not carry the multi-select overlap.** A `multi_select` answer is a
 *   set, and "the same answer" is the wrong question to ask of two sets — that
 *   term is Jaccard, scaled by `sharedOptionsMax` above. A key here naming a
 *   multi-select question is therefore also inert, and the scorer says so where
 *   it splits the two.
 */
export const NominationQuestionWeightsSchema = z.record(
  // Bounded to `profile_questions.key`'s own column width, so a key that could
  // never name a real question is refused at the write rather than stored and
  // ignored for ever.
  z.string().min(1).max(64),
  WeightSchema,
);

export type NominationQuestionWeights = z.infer<typeof NominationQuestionWeightsSchema>;
